import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Globe } from 'lucide-react';

/**
 * ShippingMethodModal — add / edit shipping method form in a modal.
 *
 * Props:
 *   editingMethod  — the method being edited, or null when creating
 *   formData       — current form state object
 *   setFormData    — state setter for formData
 *   onZoneAdd      — () => void
 *   onZoneRemove   — (index) => void
 *   onZoneUpdate   — (index, field, value) => void
 *   onSubmit       — (e) => void  (form submit handler)
 *   onClose        — () => void
 */
const ShippingMethodModal = ({
  editingMethod,
  formData,
  setFormData,
  onZoneAdd,
  onZoneRemove,
  onZoneUpdate,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content quote-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* ── Modal header ─────────────────────────────────── */}
        <div className="modal-header">
          <h2>
            {editingMethod
              ? t('admin.shipping.editTitle')
              : t('admin.shipping.newTitle')}
          </h2>
          <button onClick={onClose} className="modal-close" aria-label={t('admin.common.close')}>×</button>
        </div>

        <form onSubmit={onSubmit} className="quote-form">

          {/* ── Basic Information ───────────────────────────── */}
          <div className="form-section">
            <h3>{t('admin.shipping.basicInfo')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('admin.shipping.namePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.carrier')} *</label>
                <input
                  type="text"
                  value={formData.carrier}
                  onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                  placeholder={t('admin.shipping.carrierPlaceholder')}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('admin.shipping.descriptionPlaceholder')}
                rows={2}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.type')} *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="standard">{t('admin.shipping.typeStandard')}</option>
                  <option value="express">{t('admin.shipping.typeExpress')}</option>
                  <option value="overnight">{t('admin.shipping.typeOvernight')}</option>
                  <option value="local_pickup">{t('admin.shipping.typeLocalPickup')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.maxWeight')}</label>
                <input
                  type="number"
                  value={formData.maxWeight}
                  onChange={(e) => setFormData({ ...formData, maxWeight: parseFloat(e.target.value) })}
                  step="0.1"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.packagingCost')}</label>
                <input
                  type="number"
                  value={formData.packagingCost}
                  onChange={(e) => setFormData({ ...formData, packagingCost: parseFloat(e.target.value) })}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  {t('admin.shipping.isActive')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.trackingAvailable}
                    onChange={(e) => setFormData({ ...formData, trackingAvailable: e.target.checked })}
                  />
                  {t('admin.shipping.trackingAvailable')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.insuranceIncluded}
                    onChange={(e) => setFormData({ ...formData, insuranceIncluded: e.target.checked })}
                  />
                  {t('admin.shipping.insuranceIncluded')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.requiresSignature}
                    onChange={(e) => setFormData({ ...formData, requiresSignature: e.target.checked })}
                  />
                  {t('admin.shipping.requiresSignature')}
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.specialInstructions')}</label>
              <textarea
                value={formData.specialInstructions}
                onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                placeholder={t('admin.shipping.specialInstructionsPlaceholder')}
                rows={2}
              />
            </div>
          </div>

          {/* ── Carrier & Tracking Information ─────────────── */}
          <div
            className="form-section"
            style={{ background: '#1e293b', borderRadius: '8px', padding: '1.5rem', marginTop: '1rem' }}
          >
            <h3 style={{ color: '#38bdf8', marginBottom: '1rem' }}>
              <Globe size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              {t('admin.shipping.carrierTracking')}
            </h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.carrierCode')}</label>
                <input
                  type="text"
                  value={formData.carrierCode}
                  onChange={(e) => setFormData({ ...formData, carrierCode: e.target.value })}
                  placeholder="DHL, UPS, FEDEX, etc."
                />
                <small style={{ color: '#94a3b8', fontSize: '12px' }}>
                  {t('admin.shipping.carrierCodeHelp')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.carrierWebsite')}</label>
                <input
                  type="url"
                  value={formData.carrierWebsite}
                  onChange={(e) => setFormData({ ...formData, carrierWebsite: e.target.value })}
                  placeholder="https://www.carrier.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.trackingUrlTemplate')}</label>
              <input
                type="text"
                value={formData.trackingUrlTemplate}
                onChange={(e) => setFormData({ ...formData, trackingUrlTemplate: e.target.value })}
                placeholder="https://www.carrier.com/track?id={tracking}"
              />
              <small style={{ color: '#94a3b8', fontSize: '12px' }}>
                {t('admin.shipping.trackingUrlHelp')}
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.carrierPhone')}</label>
                <input
                  type="tel"
                  value={formData.carrierPhone}
                  onChange={(e) => setFormData({ ...formData, carrierPhone: e.target.value })}
                  placeholder="+39 800 123 456"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.carrierEmail')}</label>
                <input
                  type="email"
                  value={formData.carrierEmail}
                  onChange={(e) => setFormData({ ...formData, carrierEmail: e.target.value })}
                  placeholder="support@carrier.com"
                />
              </div>
            </div>
          </div>

          {/* ── Advanced Options ────────────────────────────── */}
          <div className="form-section" style={{ marginTop: '1rem' }}>
            <h3>{t('admin.shipping.advancedOptions')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.handlingTime')}</label>
                <input
                  type="number"
                  value={formData.handlingTime}
                  onChange={(e) => setFormData({ ...formData, handlingTime: parseInt(e.target.value) })}
                  min="0"
                />
                <small style={{ color: '#94a3b8', fontSize: '12px' }}>
                  {t('admin.shipping.handlingTimeHelp')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.minWeight')}</label>
                <input
                  type="number"
                  value={formData.minWeight}
                  onChange={(e) => setFormData({ ...formData, minWeight: parseFloat(e.target.value) })}
                  step="0.1"
                  min="0"
                />
              </div>
            </div>

            <div
              className="form-row"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}
            >
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.deliveryGuarantee}
                    onChange={(e) => setFormData({ ...formData, deliveryGuarantee: e.target.checked })}
                  />
                  {t('admin.shipping.deliveryGuarantee')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.saturdayDelivery}
                    onChange={(e) => setFormData({ ...formData, saturdayDelivery: e.target.checked })}
                  />
                  {t('admin.shipping.saturdayDelivery')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.sundayDelivery}
                    onChange={(e) => setFormData({ ...formData, sundayDelivery: e.target.checked })}
                  />
                  {t('admin.shipping.sundayDelivery')}
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.customsClearance}
                    onChange={(e) => setFormData({ ...formData, customsClearance: e.target.checked })}
                  />
                  {t('admin.shipping.customsClearance')}
                </label>
              </div>
            </div>
          </div>

          {/* ── Shipping Zones ──────────────────────────────── */}
          <div className="form-section">
            <h3>{t('admin.shipping.zones')}</h3>

            {formData.zones.map((zone, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #27272a',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  background: '#18181b',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ color: '#10b981', fontSize: '16px' }}>
                    {t('admin.shipping.zoneName')} {index + 1}
                  </h4>
                  {formData.zones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onZoneRemove(index)}
                      className="admin-btn admin-btn-danger"
                      style={{ padding: '4px 12px', fontSize: '14px' }}
                    >
                      <Trash2 size={14} />
                      {t('admin.shipping.removeZone')}
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('admin.shipping.zoneName')} *</label>
                    <input
                      type="text"
                      value={zone.name}
                      onChange={(e) => onZoneUpdate(index, 'name', e.target.value)}
                      placeholder={t('admin.shipping.zoneNamePlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('admin.shipping.countries')} *</label>
                    <input
                      type="text"
                      value={zone.countries.join(', ')}
                      onChange={(e) => onZoneUpdate(index, 'countries', e.target.value)}
                      placeholder={t('admin.shipping.countriesPlaceholder')}
                      required
                    />
                    <small style={{ color: '#71717a', fontSize: '12px', marginTop: '0.25rem', display: 'block' }}>
                      {t('admin.shipping.countriesHint')}
                    </small>
                  </div>

                  <div className="form-group">
                    <label>{t('admin.shipping.regions')}</label>
                    <input
                      type="text"
                      value={zone.regions.join(', ')}
                      onChange={(e) => onZoneUpdate(index, 'regions', e.target.value)}
                      placeholder={t('admin.shipping.regionsPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('admin.shipping.baseCost')} *</label>
                    <input
                      type="number"
                      value={zone.baseCost}
                      onChange={(e) => onZoneUpdate(index, 'baseCost', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.shipping.costPerKg')}</label>
                    <input
                      type="number"
                      value={zone.costPerKg}
                      onChange={(e) => onZoneUpdate(index, 'costPerKg', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.shipping.freeShippingThreshold')}</label>
                    <input
                      type="number"
                      value={zone.freeShippingThreshold}
                      onChange={(e) => onZoneUpdate(index, 'freeShippingThreshold', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                    <small style={{ color: '#71717a', fontSize: '12px', marginTop: '0.25rem', display: 'block' }}>
                      {t('admin.shipping.freeShippingHint')}
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('admin.shipping.estimatedDays')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        value={zone.estimatedDays.min}
                        onChange={(e) => onZoneUpdate(index, 'estimatedDays.min', parseInt(e.target.value))}
                        min="1"
                        placeholder={t('admin.shipping.minDays')}
                        style={{ width: '100px' }}
                      />
                      <span>-</span>
                      <input
                        type="number"
                        value={zone.estimatedDays.max}
                        onChange={(e) => onZoneUpdate(index, 'estimatedDays.max', parseInt(e.target.value))}
                        min="1"
                        placeholder={t('admin.shipping.maxDays')}
                        style={{ width: '100px' }}
                      />
                      <span>{t('admin.shipping.card.days')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={onZoneAdd} className="add-item-btn">
              <Plus size={16} />
              {t('admin.shipping.addZone')}
            </button>
          </div>

          {/* ── Modal footer actions ────────────────────────── */}
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="admin-btn">
              {t('admin.shipping.actions.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn-primary">
              {editingMethod
                ? t('admin.shipping.actions.update')
                : t('admin.shipping.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingMethodModal;
